import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ShoppingCart, Heart, Truck, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

gsap.registerPlugin(ScrollTrigger);

const HowToOrder = () => {
  const sectionRef = useRef(null);
  const stepsRef = useRef([]);

  const steps = [
    {
      icon: ShoppingCart,
      title: 'Place Your Order',
      description: 'Choose your flavours and quantity (minimum 5 boxes (20 doughnuts) per order).',
      color: 'bg-pink-500'
    },
    {
      icon: Heart,
      title: 'Set Your Sale Price',
      description: 'Sell each box at your chosen rate. Most partners double their return.',
      color: 'bg-teal-500'
    },
    {
      icon: Truck,
      title: 'Host Your Fundraiser',
      description: 'Sell your boxes at events, stalls, or gatherings.',
      color: 'bg-amber-500'
    },
    {
      icon: CheckCircle,
      title: 'Make an Impact',
      description: 'Keep your profits to support your projects, initiatives, or local causes.',
      color: 'bg-pink-500'
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      stepsRef.current.forEach((step, index) => {
        gsap.fromTo(step,
          { y: 50, opacity: 0, scale: 0.8 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.6,
            delay: index * 0.15,
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: step,
              start: 'top 85%',
              toggleActions: 'play none none none'
            }
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="how-to-order" ref={sectionRef} className="py-20 px-4 bg-gradient-to-br from-amber-50 to-pink-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How it works
          </h2>
          <p className="text-xl text-gray-600">
            Four simple steps to make a difference
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                ref={el => stepsRef.current[index] = el}
                className="step-card"
              >
                <Card className="h-full transition-all duration-300 hover:shadow-xl relative group">
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 ${step.color} rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}>
                      <Icon className="text-white" size={32} />
                    </div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="text-3xl font-bold text-gray-200">{index + 1}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <p className="text-lg font-semibold text-amber-700 bg-amber-100 inline-block px-6 py-3 rounded-full">
            Wholesale pricing available for all orders!
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowToOrder;